import type { SettingsResponse } from "@/lib/api/types";
import type { AIProvider } from "@/lib/mapper/types";

/**
 * Provider choices shown in the AI rule editor.
 *
 * "claude-local" (Claude Code on the user's own machine, billed against their
 * Claude subscription) is only offered when the backend reports
 * `capabilities.localClaude` — i.e. it runs locally with AI_LOCAL_CLI=true.
 * Hosted deployments never list it. A rule that already uses it on a backend
 * without the capability keeps a disabled entry so the select still shows
 * what the rule is set to.
 */
export type ProviderOption = {
  value: AIProvider;
  label: string;
  disabled?: boolean;
};

export const LOCAL_CLAUDE_LABEL = "Claude (your subscription, local)";

export function providerOptions(opts: {
  localClaude: boolean;
  current?: AIProvider;
}): ProviderOption[] {
  const out: ProviderOption[] = [
    { value: "anthropic", label: "Anthropic API" },
    { value: "openai", label: "OpenAI API" },
    { value: "openrouter", label: "OpenRouter (DeepSeek, Qwen, Gemini…)" },
  ];
  if (opts.localClaude) {
    out.push({ value: "claude-local", label: LOCAL_CLAUDE_LABEL });
  } else if (opts.current === "claude-local") {
    out.push({
      value: "claude-local",
      label: `${LOCAL_CLAUDE_LABEL}, unavailable on this server`,
      disabled: true,
    });
  }
  return out;
}

/** True only when the backend explicitly reports the local-Claude capability. */
export function localClaudeCapability(
  resp: Pick<SettingsResponse, "capabilities"> | null | undefined,
): boolean {
  return resp?.capabilities?.localClaude === true;
}
